/**
 * REVCLOUD Object-mode outline tracing (REVCLOUD-MODES slice).
 *
 * AutoCAD's REVCLOUD "Object" option converts an existing CLOSED object
 * — circle, ellipse, closed polyline, or closed spline — into a revision
 * cloud that traces the source outline (Autodesk REVCLOUD command
 * reference: "Object — Specifies an object to be converted to a revision
 * cloud"). AutoCAD replaces the source object with the new cloud by
 * default and then prompts "Reverse direction [Yes/No]".
 *
 * This module computes the CLOUD VERTEX CHAIN for a given source entity:
 * the renderer (`scene3d/adapters/geom/revisionCloudGeometry.ts`)
 * replaces every edge of that chain with `max(1, round(len/arcLength))`
 * half-circle bulges, so curved source outlines are sampled here at
 * ~`arcChordLength` spacing (one cloud bump per sample step — matching
 * AutoCAD where each arc of the converted cloud spans roughly the
 * configured arc length), while straight polyline segments keep their
 * exact corner vertices (the renderer subdivides them itself).
 *
 * Returns `null` when the entity is not a supported closed outline —
 * callers surface AutoCAD's rejection path ("The selected object is not
 * a valid closed object") instead of guessing.
 *
 * Pure math, no document access — lives in the command-engine CAD core
 * (ПРИОРИТЕТ #1: typed model before UI) and is unit-tested by
 * `revisionCloud.objectMode.test.ts`.
 */

import {
  fitPointsToControlPoints,
  sampleNurbs,
  type DrawingEntity,
  type SplinePoint,
} from "@dxf-ai-editor/drawing-model";

import { sampleBulgeIntermediatePoints } from "./pathSampling";

export type RevcloudOutlinePoint = { x: number; y: number };

const TAU = Math.PI * 2;
const EPS = 1e-9;

/**
 * ONE truth for the revision-cloud arc-chord floor.
 *
 * The renderer (`scene3d/adapters/geom/revisionCloudGeometry.ts`) floors
 * the approximate arc length at 1 drawing unit before computing the bulge
 * count, so every consumer that samples a cloud outline at ~chord spacing
 * (this tracer, the draft hit-test, the renderer itself) MUST apply the
 * SAME floor — a sub-1 chord here would produce a sampling the renderer
 * never matches.
 */
export const MIN_REVCLOUD_ARC_CHORD = 1;

/** Clamp an arc-chord length to the shared {@link MIN_REVCLOUD_ARC_CHORD}
 *  floor — the single clamp the tracer, renderer and draft consumers use. */
export function clampRevcloudChord(arcChordLength: number): number {
  return Math.max(MIN_REVCLOUD_ARC_CHORD, arcChordLength);
}

/** Drop consecutive duplicates + a trailing point that closes onto the
 *  first vertex (the cloud chain is implicitly closed). */
function dedupeClosedChain(
  points: RevcloudOutlinePoint[],
): RevcloudOutlinePoint[] {
  const out: RevcloudOutlinePoint[] = [];
  for (const p of points) {
    const prev = out[out.length - 1];
    if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < EPS) continue;
    out.push(p);
  }
  const first = out[0];
  const last = out[out.length - 1];
  if (
    out.length > 1 &&
    first &&
    last &&
    Math.hypot(first.x - last.x, first.y - last.y) < EPS
  ) {
    out.pop();
  }
  return out;
}

function circleOutline(
  center: RevcloudOutlinePoint,
  radius: number,
  chord: number,
): RevcloudOutlinePoint[] | null {
  if (!(radius > EPS)) return null;
  const circumference = TAU * radius;
  const count = Math.max(3, Math.ceil(circumference / chord));
  const out: RevcloudOutlinePoint[] = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * TAU;
    out.push({
      x: center.x + radius * Math.cos(a),
      y: center.y + radius * Math.sin(a),
    });
  }
  return out;
}

function ellipseOutline(
  entity: Extract<DrawingEntity, { type: "Ellipse" }>,
  chord: number,
): RevcloudOutlinePoint[] | null {
  // Only a FULL ellipse is a closed object (AutoCAD rejects elliptical
  // arcs for REVCLOUD Object mode).
  const sweep = Math.abs(entity.endParam - entity.startParam);
  if (sweep < TAU - 1e-6) return null;
  const a = Math.hypot(entity.majorAxisEndpoint.x, entity.majorAxisEndpoint.y);
  const b = a * entity.ratio;
  if (!(a > EPS) || !(b > EPS)) return null;
  // Ramanujan approximation of the ellipse perimeter — drives sample count.
  const h = ((a - b) / (a + b)) ** 2;
  const perimeter =
    Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
  const count = Math.max(3, Math.ceil(perimeter / chord));
  const ux = entity.majorAxisEndpoint.x / a;
  const uy = entity.majorAxisEndpoint.y / a;
  const out: RevcloudOutlinePoint[] = [];
  for (let i = 0; i < count; i++) {
    const t = (i / count) * TAU;
    const ca = a * Math.cos(t);
    const sb = b * Math.sin(t);
    out.push({
      x: entity.center.x + ca * ux - sb * uy,
      y: entity.center.y + ca * uy + sb * ux,
    });
  }
  return out;
}

function lwpolylineOutline(
  entity: Extract<DrawingEntity, { type: "LWPOLYLINE" }>,
  chord: number,
): RevcloudOutlinePoint[] | null {
  if (!entity.closed || entity.vertices.length < 3) return null;
  const vs = entity.vertices;
  const out: RevcloudOutlinePoint[] = [];
  for (let i = 0; i < vs.length; i++) {
    const v1 = vs[i]!;
    const v2 = vs[(i + 1) % vs.length]!;
    out.push({ x: v1.x, y: v1.y });
    // Bulged segments are subdivided into ~chord-length steps so the
    // cloud follows the source arc; straight segments keep their exact
    // corners (the renderer subdivides straight edges itself).
    if (Math.abs(v1.bulge) >= 1e-12) {
      out.push(
        ...sampleBulgeIntermediatePoints(
          { x: v1.x, y: v1.y },
          { x: v2.x, y: v2.y },
          v1.bulge,
          chord,
        ),
      );
    }
  }
  return out;
}

function splineOutline(
  entity: Extract<DrawingEntity, { type: "Spline" }>,
  chord: number,
): RevcloudOutlinePoint[] | null {
  if (!entity.closed) return null;
  // Resolve a NURBS basis: control-points mode directly, fit-points mode
  // via the same interpolation AutoCAD's SPLINE FIT path uses.
  let controlPoints: SplinePoint[] = entity.controlPoints;
  let knots: number[] = entity.knots;
  let weights: number[] | undefined =
    entity.weights.length > 0 ? entity.weights : undefined;
  const degree = entity.degree;
  if (controlPoints.length === 0 && entity.fitPoints.length >= 3) {
    const fitted = fitPointsToControlPoints(
      entity.fitPoints,
      entity.startTangent,
      entity.endTangent,
      degree,
    );
    controlPoints = fitted.controlPoints;
    knots = fitted.knots;
    weights = undefined;
  }
  if (controlPoints.length < 3 || knots.length === 0) return null;
  // Estimate curve length from the control polygon (upper bound) to pick
  // the sample density, then sample the NURBS uniformly in parameter.
  let polygonLength = 0;
  for (let i = 1; i < controlPoints.length; i++) {
    const p = controlPoints[i]!;
    const q = controlPoints[i - 1]!;
    polygonLength += Math.hypot(p.x - q.x, p.y - q.y);
  }
  if (!(polygonLength > EPS)) return null;
  const count = Math.max(4, Math.ceil(polygonLength / chord) + 1);
  const samples = sampleNurbs(controlPoints, knots, weights, degree, count);
  return samples.map((s) => ({ x: s.x, y: s.y }));
}

/**
 * Trace the outline of a closed source entity into the revision-cloud
 * vertex chain. Supported sources (AutoCAD REVCLOUD Object option):
 * Circle, full Ellipse, closed LWPOLYLINE (incl. RECTANG output and
 * bulged segments), closed Spline. Anything else — including OPEN
 * polylines/splines and elliptical arcs — returns `null` so the caller
 * can reject the pick the way AutoCAD does.
 */
export function revisionCloudOutlineFromSource(
  entity: DrawingEntity,
  arcChordLength: number,
): RevcloudOutlinePoint[] | null {
  const chord = clampRevcloudChord(arcChordLength);
  let raw: RevcloudOutlinePoint[] | null = null;
  switch (entity.type) {
    case "Circle":
      raw = circleOutline(entity.center, entity.radius, chord);
      break;
    case "Ellipse":
      raw = ellipseOutline(entity, chord);
      break;
    case "LWPOLYLINE":
      raw = lwpolylineOutline(entity, chord);
      break;
    case "Spline":
      raw = splineOutline(entity, chord);
      break;
    default:
      return null;
  }
  if (!raw) return null;
  const chain = dedupeClosedChain(raw);
  return chain.length >= 3 ? chain : null;
}

/** Minimal space shape the Object-mode source resolver walks —
 *  structurally satisfied by drawing-model `DrawingDocument["spaces"]`. */
export type RevcloudSourceSpace = {
  id: string;
  entities: ReadonlyArray<DrawingEntity>;
};

export type RevcloudObjectSourceResolution = {
  /** The closed source entity the cloud replaces. */
  source: DrawingEntity;
  /** Space the source lives in — the conversion happens in place, so the
   *  cloud lands in the SOURCE entity's space (AutoCAD behaviour). */
  spaceId: string;
  /** Traced cloud vertex chain (sampled at ~`arcChordLength` spacing). */
  outline: RevcloudOutlinePoint[];
};

/**
 * ONE validity truth for REVCLOUD Object-mode commands.
 *
 * Resolves `sourceEntityId` against the document spaces and traces its
 * closed outline. Returns `null` when the id is missing, unknown, or the
 * entity is not a supported closed outline (open polyline / spline,
 * elliptical arc, Line, ...) — AutoCAD rejects the pick with "The
 * selected object is not a valid closed object".
 *
 * BOTH `geometry/create-revcloud` reducer arms call this predicate: the
 * geometry document reducer (decides NO_MUTATIONS) AND the shell reducer
 * (decides whether to append/select a preview row). Keeping a single
 * predicate guarantees the shell can never append a preview for a command
 * the document reducer no-ops — a divergence that previously left a
 * phantom cloud in the UI that did not exist in the document (the resync
 * after a no-op mutation set REUSES shell entities, it does not rebuild).
 */
export function resolveRevcloudObjectSource(
  spaces: ReadonlyArray<RevcloudSourceSpace>,
  sourceEntityId: string | undefined,
  arcChordLength: number,
): RevcloudObjectSourceResolution | null {
  if (!sourceEntityId) return null;
  for (const space of spaces) {
    const source = space.entities.find((e) => e.id === sourceEntityId);
    if (!source) continue;
    const outline = revisionCloudOutlineFromSource(source, arcChordLength);
    if (!outline) return null;
    return { source, spaceId: space.id, outline };
  }
  return null;
}
